/**
 * Text-free coordinates from the authenticated Stage 1 DB read. This module
 * does not admit LiveKit senders, hydrate state, or choose visible members.
 * Its caller must supply the actual selected revision and accepted capture
 * order; a missing fact leaves all per-member words available as unverified.
 */
import { validateSourceTurnV1, type SourceTurnV1 } from './contracts';
import type { LaunchLanguageCode } from './launchLanguages';
import {
  validateTranslationRevisionRecord,
  type AttachedTranslationRevisionV1,
} from './revisionEnvelope';

export const ENGINE_COORDINATE_CONTRACT_VERSION = 'live-translation.engine-coordinate.v1';

export interface EngineCoordinateV1 {
  contract_version: typeof ENGINE_COORDINATE_CONTRACT_VERSION;
  call_id: string;
  translation_revision_id: string;
  source_turn_id: string;
  translation_session_record_id: string;
  source_stable_participant_id: string;
  source_epoch: number;
  source_track_sid: string;
  target_language: LaunchLanguageCode;
  assignment_generation: number;
  route_generation: number;
  allocation_group_id: string | null;
  engine_range_start: number;
  engine_range_end: number;
}

export interface SelectedCoordinateBinding {
  source_turn_id: string;
  target_language: LaunchLanguageCode;
  selected_revision_id: string;
  selection_generation: number;
}

export type CoordinateRefusal =
  | 'coordinate_shape'
  | 'source_invalid'
  | 'revision_invalid'
  | 'selection_invalid'
  | 'lineage_mismatch'
  | 'range_invalid';

export type CoordinateValidation =
  | { valid: true; coordinate: EngineCoordinateV1 }
  | { valid: false; reason: CoordinateRefusal };

const COORDINATE_KEYS: ReadonlySet<string> = new Set([
  'contract_version', 'call_id', 'translation_revision_id', 'source_turn_id',
  'translation_session_record_id', 'source_stable_participant_id',
  'source_epoch', 'source_track_sid', 'target_language',
  'assignment_generation', 'route_generation', 'allocation_group_id',
  'engine_range_start', 'engine_range_end',
]);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const NIL_UUID = '00000000-0000-0000-0000-000000000000';

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function safeInteger(value: unknown, minimum: number): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= minimum;
}

function codePointLength(value: string): number {
  // The string iterator counts Unicode code points, including astral pairs,
  // without materializing a character array for a complete long revision.
  let count = 0;
  for (const _codePoint of value) count += 1;
  return count;
}

/** Refuse partial/extended or guessed coordinates before they reach a view. */
export function validateEngineCoordinateForRevision(
  raw: unknown,
  source: SourceTurnV1,
  selectedRevision: AttachedTranslationRevisionV1,
): CoordinateValidation {
  if (!validateSourceTurnV1(source).valid) {
    return { valid: false, reason: 'source_invalid' };
  }
  const revision = validateTranslationRevisionRecord(selectedRevision);
  if (!revision.valid || revision.attachment === null) {
    return { valid: false, reason: 'revision_invalid' };
  }
  return validateEngineCoordinateBinding(raw, source, selectedRevision);
}

/** Shared checks after the actual source/revision contracts have passed. */
function validateEngineCoordinateBinding(
  raw: unknown, source: SourceTurnV1, selectedRevision: AttachedTranslationRevisionV1,
): CoordinateValidation {
  if (!record(raw) || raw.contract_version !== ENGINE_COORDINATE_CONTRACT_VERSION ||
      Object.keys(raw).length !== COORDINATE_KEYS.size ||
      Object.keys(raw).some((key) => !COORDINATE_KEYS.has(key))) {
    return { valid: false, reason: 'coordinate_shape' };
  }
  for (const key of [
    'call_id', 'translation_revision_id', 'source_turn_id',
    'translation_session_record_id',
  ] as const) {
    const value = raw[key];
    if (typeof value !== 'string' || !UUID.test(value) || value === NIL_UUID) {
      return { valid: false, reason: 'coordinate_shape' };
    }
  }
  const group = raw.allocation_group_id;
  if (group !== null && (typeof group !== 'string' || !UUID.test(group) || group === NIL_UUID)) {
    return { valid: false, reason: 'coordinate_shape' };
  }
  if (typeof raw.source_stable_participant_id !== 'string' ||
      raw.source_stable_participant_id.length === 0 ||
      typeof raw.source_track_sid !== 'string' || raw.source_track_sid.length === 0 ||
      !safeInteger(raw.source_epoch, 1) ||
      !safeInteger(raw.assignment_generation, 0) ||
      !safeInteger(raw.route_generation, 0)) {
    return { valid: false, reason: 'coordinate_shape' };
  }
  if (
    raw.call_id !== source.call_id ||
    raw.call_id !== selectedRevision.call_id ||
    raw.translation_revision_id !== selectedRevision.translation_revision_id ||
    raw.source_turn_id !== source.source_turn_id ||
    raw.source_turn_id !== selectedRevision.source_turn_id ||
    raw.source_stable_participant_id !== source.stable_participant_id ||
    raw.source_epoch !== source.source_epoch ||
    raw.source_track_sid !== source.source_track_sid ||
    raw.target_language !== selectedRevision.target_language ||
    raw.assignment_generation !== selectedRevision.assignment_generation ||
    raw.route_generation !== selectedRevision.route_generation ||
    selectedRevision.origin !== 'engine' ||
    raw.allocation_group_id !== selectedRevision.allocation_group_id ||
    raw.engine_range_start !== selectedRevision.engine_range_start ||
    raw.engine_range_end !== selectedRevision.engine_range_end
  ) {
    return { valid: false, reason: 'lineage_mismatch' };
  }
  const start = raw.engine_range_start;
  const end = raw.engine_range_end;
  if (!safeInteger(start, 0) || !safeInteger(end, start) ||
      end - start !== codePointLength(selectedRevision.text)) {
    return { valid: false, reason: 'range_invalid' };
  }
  return { valid: true, coordinate: raw as unknown as EngineCoordinateV1 };
}

/** A coordinate belongs to its persisted revision even when it is unselected.
 * Only this selected wrapper asks for an actual selection; callers must never
 * fabricate one merely to retain an authenticated historical sidecar. */
export function validateEngineCoordinateV1(
  raw: unknown, source: SourceTurnV1, selectedRevision: AttachedTranslationRevisionV1,
  selection: SelectedCoordinateBinding,
): CoordinateValidation {
  if (!validateSourceTurnV1(source).valid) return { valid: false, reason: 'source_invalid' };
  const revision = validateTranslationRevisionRecord(selectedRevision);
  if (!revision.valid || revision.attachment === null) return { valid: false, reason: 'revision_invalid' };
  if (!record(selection) || !safeInteger(selection.selection_generation, 0) ||
      selection.selected_revision_id !== selectedRevision.translation_revision_id ||
      selection.source_turn_id !== source.source_turn_id ||
      selection.target_language !== selectedRevision.target_language) {
    return { valid: false, reason: 'selection_invalid' };
  }
  return validateEngineCoordinateBinding(raw, source, selectedRevision);
}

export interface SelectedVisibleMember {
  source_turn: SourceTurnV1;
  selected_revision: AttachedTranslationRevisionV1 | null;
  selection: SelectedCoordinateBinding | null;
  coordinate: unknown;
}

export interface VisibleMemberWords {
  source_turn_id: string;
  source_text: string;
  selected_revision_id: string | null;
  translation_text: string | null;
}

export type ExactAssembly =
  | { verified: true; text: string; members: VisibleMemberWords[] }
  | {
      verified: false;
      reason:
        | 'lineage_incomplete' | 'member_missing' | 'duplicate_member'
        | 'provisional' | 'coordinate_invalid' | 'namespace_mismatch'
        | 'range_discontiguous';
      members: VisibleMemberWords[];
    };

/**
 * Input order is the caller's accepted visible-member order, never wall-clock
 * sorted here. `lineageComplete` must come from the actual capture resolver.
 */
export function assembleExactSelectedEngineRange(input: {
  members: readonly SelectedVisibleMember[];
  lineageComplete: boolean;
  unresolvedVisibleMemberCount: number;
}): ExactAssembly {
  const words = input.members.map((member): VisibleMemberWords => ({
    source_turn_id: member.source_turn.source_turn_id,
    source_text: member.source_turn.text,
    selected_revision_id: member.selected_revision?.translation_revision_id ?? null,
    translation_text: member.selected_revision?.text ?? null,
  }));
  const refuse = (reason: Extract<ExactAssembly, { verified: false }>['reason']): ExactAssembly =>
    ({ verified: false, reason, members: words });
  if (!input.lineageComplete || !safeInteger(input.unresolvedVisibleMemberCount, 0) ||
      input.unresolvedVisibleMemberCount !== 0 || input.members.length === 0) {
    return refuse('lineage_incomplete');
  }
  const seen = new Set<string>();
  let namespace: readonly unknown[] | null = null;
  let previousEnd: number | null = null;
  const rawTexts: string[] = [];
  for (const member of input.members) {
    const sourceId = member.source_turn.source_turn_id;
    if (seen.has(sourceId)) return refuse('duplicate_member');
    seen.add(sourceId);
    if (!member.selected_revision || !member.selection) return refuse('member_missing');
    if (member.selected_revision.attachment_confidence !== 'exact') return refuse('provisional');
    const checked = validateEngineCoordinateV1(
      member.coordinate, member.source_turn, member.selected_revision, member.selection,
    );
    if (!checked.valid) return refuse('coordinate_invalid');
    const coordinate = checked.coordinate;
    const candidate = [
      coordinate.call_id, coordinate.translation_session_record_id,
      coordinate.source_stable_participant_id, coordinate.source_epoch,
      coordinate.source_track_sid, coordinate.target_language,
      coordinate.assignment_generation, coordinate.route_generation,
      coordinate.allocation_group_id,
    ];
    if (namespace !== null && candidate.some((value, index) => value !== namespace![index])) {
      return refuse('namespace_mismatch');
    }
    namespace = candidate;
    if (previousEnd !== null && coordinate.engine_range_start !== previousEnd) {
      return refuse('range_discontiguous');
    }
    previousEnd = coordinate.engine_range_end;
    rawTexts.push(member.selected_revision.text);
  }
  return { verified: true, text: rawTexts.join(''), members: words };
}
